/**
 * The passage a documentation hit shows in the palette, and the text the search
 * page shows for one (DX-341).
 *
 * The search index answers with the matched words wrapped in a pair of tags it
 * chooses itself, and how much text surrounds them depends on the engine: the
 * Postgres one used in production returns a fragment of a few dozen words, the
 * in-memory one used locally returns the whole section. A palette row has room
 * for two lines, so either answer is cut to a window that starts a little
 * before the first match — a passage that opens on the matched word reads as a
 * fragment, one that opens a sentence earlier reads as a quotation.
 */

/** A run of the passage, and whether it is one of the words that matched. */
export type ExcerptPart = { text: string; hit: boolean };

/** How long a passage is, in characters — about two lines of a palette row. */
const LENGTH = 160;

/** How much comes before the first matched word. */
const LEAD = 40;

/**
 * How far an edge of the passage moves to reach a word boundary. Further than
 * that the word is a URL, a path or a hash, and moving past it would push the
 * matched word out of the passage at the front, or most of the passage out at
 * the back — so the edge cuts the long word instead.
 */
const SNAP = 20;

/**
 * What the index reads as text and a reader does not.
 *
 * TechDocs builds its index from the rendered page, so two pieces of the page's
 * furniture arrive as words. The `¶` beside every heading is its permalink
 * anchor. And a code block numbers its lines in a column of its own, which
 * reads as "1 2 3 4 5 6 7" in front of the code — a passage through one opened
 * "1 2 3 4 5 6 7 # My Service". A run counting up from one is taken for that
 * column; a lone "1" stays, because it is as likely to be "Step 1".
 *
 * It is a guess, and it can be wrong: "Replicas 1 2 3 per region" loses its
 * numbers too. Cleaning once, where the index is built, is DX-411.
 */
const unfurnish = (text: string) =>
  text
    .replace(/¶/g, ' ')
    .replace(/(^|\s)(1(?:\s+\d+)+)(?=\s|$)/g, (match, lead, run: string) => {
      const numbers = run.split(/\s+/);
      let counted = 0;
      while (
        counted < numbers.length &&
        Number(numbers[counted]) === counted + 1
      ) {
        counted++;
      }
      return counted < 2 ? match : `${lead}${numbers.slice(counted).join(' ')}`;
    });

/**
 * The index's text without that furniture, its tags left where they are — for
 * the search page, which marks the matched words itself and needs only the
 * text cleaned (DX-341). The same passage then reads the same in both places.
 */
export const cleanIndexText = (text: string) =>
  unfurnish(text).replace(/\s+/g, ' ').trim();

/** Splits the index's tagged text into runs, without the tags. */
const split = (text: string, preTag?: string, postTag?: string) => {
  const parts: ExcerptPart[] = [];
  let rest = text;
  while (preTag && postTag && rest) {
    const open = rest.indexOf(preTag);
    const close = open < 0 ? -1 : rest.indexOf(postTag, open + preTag.length);
    // An opening tag with no closing one is the index's text cut short, not a
    // match running to the end: what follows it is ordinary text.
    if (close < 0) {
      break;
    }
    if (open > 0) {
      parts.push({ text: rest.slice(0, open), hit: false });
    }
    parts.push({ text: rest.slice(open + preTag.length, close), hit: true });
    rest = rest.slice(close + postTag.length);
  }
  if (rest) {
    parts.push({ text: rest, hit: false });
  }
  // A tag the index left without its pair — an opening one at a cut, a
  // closing one after overlapping matches — is not text to show.
  const untag = (t: string) =>
    preTag && postTag ? t.split(preTag).join('').split(postTag).join('') : t;
  // Documents carry the line breaks and indentation of their source; in a row
  // they are only gaps in the sentence.
  const cleaned = parts
    .map(p => ({
      ...p,
      text: unfurnish(p.hit ? p.text : untag(p.text)).replace(/\s+/g, ' '),
    }))
    .filter(p => p.text);
  // What was stripped from the front leaves a gap the passage would open on.
  if (cleaned.length) {
    cleaned[0] = { ...cleaned[0], text: cleaned[0].text.trimStart() };
  }
  return cleaned;
};

/**
 * The window of `text` worth showing, as runs a row can mark the hits in.
 *
 * Without tags the passage is the opening of the text: a hit the index could
 * not mark is still a hit, and the start of the section is what it is about.
 */
export const docsExcerpt = (
  text: string,
  preTag?: string,
  postTag?: string,
): ExcerptPart[] => {
  const parts = split(text, preTag, postTag);
  const whole = parts.map(p => p.text).join('');
  const firstHit = parts.findIndex(p => p.hit);
  const hitAt =
    firstHit < 0
      ? 0
      : parts.slice(0, firstHit).reduce((n, p) => n + p.text.length, 0);
  const hitEnd = firstHit < 0 ? 0 : hitAt + parts[firstHit].text.length;

  // Both edges on a word boundary, so the passage neither opens nor ends on
  // half of one — unless the boundary is further than SNAP away.
  let start = Math.max(0, hitAt - LEAD);
  if (start > 0) {
    const space = whole.lastIndexOf(' ', start);
    if (space >= 0 && start - space <= SNAP) {
      start = space + 1;
    }
  }
  let end = Math.min(whole.length, start + LENGTH);
  if (end < whole.length) {
    const space = whole.lastIndexOf(' ', end);
    if (space > Math.max(start, hitEnd) && end - space <= SNAP) {
      end = space;
    }
  }

  const out: ExcerptPart[] = [];
  let at = 0;
  for (const part of parts) {
    const from = Math.max(start, at);
    const to = Math.min(end, at + part.text.length);
    if (from < to) {
      out.push({ text: part.text.slice(from - at, to - at), hit: part.hit });
    }
    at += part.text.length;
  }
  // The ellipses are runs of their own: on a matched word at an edge, folded
  // into it, they were drawn as part of the word.
  if (start > 0 && out.length) {
    out[0] = { ...out[0], text: out[0].text.trimStart() };
    out.unshift({ text: '…', hit: false });
  }
  if (end < whole.length && out.length) {
    const last = out[out.length - 1];
    out[out.length - 1] = { ...last, text: last.text.trimEnd() };
    out.push({ text: '…', hit: false });
  }
  return out;
};
